import React, { useCallback, useEffect } from 'react';
import { useEditorStore } from '../store/editorStore';
import { ZOOM_STEP, useCanvasStore } from '../store/canvasStore';
import { redoEditor, undoEditor } from '../store/historyActions';
import { mergeFloorPlanDraft } from '../draftStorage';
import DrawingCanvas from './Canvas/DrawingCanvas';
import TopBar from './Panels/TopBar';
import LeftToolbar from './Panels/LeftToolbar';
import SymbolPanel from './Panels/SymbolPanel';
import PropertiesPanel from './Panels/PropertiesPanel';
import LayersPanel from './Panels/LayersPanel';
import StatusBar from './Panels/StatusBar';
import type { SaveStatus } from './Panels/StatusBar';
import ExportModal from './Modals/ExportModal';
import type { CanvasShape, FloorPlan, ToolId } from '../types';
import type { UserProfile } from '../types';

const TOOL_SHORTCUTS: Record<string, ToolId> = {
  v: 'select',
  r: 'room',
  p: 'polygon',
  w: 'wall',
  d: 'dimension',
  t: 'text',
  s: 'symbol',
  e: 'eraser',
};

interface FloorPlanEditorProps {
  floorPlan: FloorPlan;
  user: UserProfile;
  onUpdate: (fp: FloorPlan) => void;
  onDelete: () => void;
  onBackToProjects: () => void;
  onCreditsChange: (delta: number) => void;
  saveStatus?: SaveStatus;
}

const FloorPlanEditor: React.FC<FloorPlanEditorProps> = ({
  floorPlan,
  user,
  onUpdate,
  onDelete,
  onBackToProjects,
  onCreditsChange,
  saveStatus = 'saved',
}) => {
  const [showExportModal, setShowExportModal] = React.useState(false);
  const { shapes, setShapes } = useCanvasStore();

  useEffect(() => {
    const { plan, restored } = mergeFloorPlanDraft(floorPlan);
    const data = plan.canvas_data as { shapes?: CanvasShape[] } | null;
    const next = data?.shapes && Array.isArray(data.shapes) ? data.shapes : [];
    setShapes(next);
    if (restored) onUpdate(plan);
    // Reload only when opening a different plan. Later edits stay in the canvas store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorPlan.id, setShapes]);

  const handleCanvasChange = useCallback(() => {
    const { shapes: s } = useCanvasStore.getState();
    onUpdate({ ...floorPlan, canvas_data: { shapes: s }, updated_at: new Date().toISOString() });
  }, [floorPlan, onUpdate]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      const key = e.key.toLowerCase();

      if (e.metaKey || e.ctrlKey) {
        if (key === 'z') {
          e.preventDefault();
          if (e.shiftKey) {
            if (redoEditor()) handleCanvasChange();
          } else if (undoEditor() === 'canvas') {
            handleCanvasChange();
          }
        } else if (key === 'y') {
          e.preventDefault();
          if (redoEditor()) handleCanvasChange();
        }
        return;
      }

      if (e.key === '+' || e.key === '=' || e.key === '-') {
        e.preventDefault();
        useCanvasStore.getState().zoomBy(e.key === '-' ? 1 / ZOOM_STEP : ZOOM_STEP);
        return;
      }

      if (key === 'backspace' && useEditorStore.getState().draftWallPoints?.length) {
        e.preventDefault();
        undoEditor();
        return;
      }

      if (key === 'delete' || key === 'backspace') {
        const ids = useEditorStore.getState().selectedIds;
        if (ids.length) {
          e.preventDefault();
          const cs = useCanvasStore.getState();
          ids.forEach((id) => cs.removeShape(id));
          cs.pushHistory();
          useEditorStore.getState().setSelectedIds([]);
          handleCanvasChange();
        }
        return;
      }

      const tool = TOOL_SHORTCUTS[key];
      if (tool) useEditorStore.getState().setActiveTool(tool);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleCanvasChange]);

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#111118' }}>
      <TopBar
        floorPlan={floorPlan}
        onUpdate={onUpdate}
        onBackToProjects={onBackToProjects}
        onExport={() => setShowExportModal(true)}
        onHistoryChange={handleCanvasChange}
      />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="flex flex-col w-14 shrink-0 border-r" style={{ borderColor: '#2e2e40', backgroundColor: '#1c1c28' }}>
          <LeftToolbar />
          <LayersPanel />
        </div>
        <div className="flex-1 flex min-w-0 relative">
          <DrawingCanvas onCanvasChange={handleCanvasChange} />
        </div>
        <div className="flex flex-col w-64 shrink-0 border-l overflow-y-auto" style={{ borderColor: '#2e2e40', backgroundColor: '#1c1c28' }}>
          <SymbolPanel />
          <PropertiesPanel onCommit={handleCanvasChange} />
        </div>
      </div>
      <StatusBar saveStatus={saveStatus} />
      {showExportModal && (
        <ExportModal
          floorPlan={floorPlan}
          user={user}
          onClose={() => setShowExportModal(false)}
          onSuccess={(used) => onCreditsChange(-used)}
        />
      )}
    </div>
  );
};

export default FloorPlanEditor;
